import { useTranslation } from 'react-i18next'

/**
 * What a dot is saying. Both look the same (red, so they are seen), but each
 * reads out its own meaning to a screen reader.
 */
export type DotKind =
  /** Something here is wrong and you have to fix it — an incomplete profile. */
  | 'attention'
  /** Something arrived since you last looked — unread news, an open event. */
  | 'new'

const LABEL: Record<DotKind, string> = {
  attention: 'profile.navWarningLabel',
  new: 'nav.newLabel',
}

/** `lg` hangs off an icon in the bottom bar; `md` sits beside a text label. */
const SIZE = { md: 'h-2.5 w-2.5', lg: 'h-3 w-3' } as const

/**
 * The marker the nav uses instead of a number.
 *
 * A count is a promise that the number means something you can work through.
 * Three unread posts is not a queue, so the nav says "there is something here"
 * and leaves it at that. `className` carries the caller's positioning: the
 * pills lay it out in a row, the tab bar hangs it off the icon.
 */
export function NavDot({
  kind,
  size = 'md',
  className = '',
}: {
  kind: DotKind
  size?: keyof typeof SIZE
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className={`${SIZE[size]} shrink-0 rounded-full bg-danger ${className}`}
      aria-label={t(LABEL[kind])}
      role="img"
    />
  )
}
